export interface ProgressEvent {
  lengthComputable: boolean
  loaded: number
  total: number
  percentage: number
}

export interface UploadOptions {
  file: File
  signedUrl: string
  onProgress?: (progressEvent: ProgressEvent) => void
  onUploaded?: () => void
  onError?: (error: Error) => void
}

export interface UploadResult {
  promise: Promise<void>
  cancelUpload: () => void
}

export const uploadFileWithProgress = ({
  file,
  signedUrl,
  onProgress,
  onUploaded,
  onError,
}: UploadOptions): UploadResult => {
  if (!file || !signedUrl) {
    throw new Error('File and Signed URL are required.')
  }

  const controller = new AbortController()
  const { signal } = controller

  const uploadPromise = new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', signedUrl, true)
    xhr.setRequestHeader('Content-Type', file.type)

    xhr.upload.onprogress = (event: Omit<ProgressEvent, 'percentage'>) => {
      if (event.lengthComputable) {
        const percentage = Math.round((event.loaded * 100) / event.total)

        onProgress?.({
          lengthComputable: true,
          loaded: event.loaded,
          total: event.total,
          percentage,
        })
      }
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        onUploaded?.()
        resolve()
      } else {
        const error = new Error('Failed to upload attachments.')
        onError?.(error)
        reject(error)
      }
    }

    xhr.onerror = () => {
      const error = new Error('Error uploading attachments.')
      onError?.(error)
      reject(error)
    }

    signal.addEventListener('abort', () => {
      xhr.abort()
      const error = new Error('Upload was cancelled.')
      onError?.(error)
      reject(error)
    })

    xhr.send(file)
  })

  const cancelUpload = () => {
    controller.abort()
  }

  return {
    promise: uploadPromise,
    cancelUpload,
  }
}
